import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useSeasonContext } from '../context/SeasonContext';
import { formatDateEST, formatTimeWithTimezone } from '../utils/timezoneUtils';
import '../styles/ScoresDisplay.css';

function ScoresDisplay() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [activeTab, setActiveTab] = useState('upcoming');
  const { selectedSeason, setSelectedSeason, availableSeasons } = useSeasonContext();

  useEffect(() => {
    if (selectedSeason) {
      fetchGames(selectedSeason.id);
    }
  }, [selectedSeason, selectedWeek]);

  const fetchGames = async (seasonId) => {
    try {
      setLoading(true);
      let query = supabase
        .from('games')
        .select(`
          id,
          week,
          scheduled_date,
          is_completed,
          team1_id,
          team2_id,
          highlights_url,
          game_scores (
            team1_score,
            team2_score
          ),
          team1:teams!games_team1_id_fkey(id, name, abbreviation, logo_url),
          team2:teams!games_team2_id_fkey(id, name, abbreviation, logo_url)
        `)
        .eq('season_id', seasonId);

      if (selectedWeek !== 'all') {
        query = query.eq('week', parseInt(selectedWeek));
      }

      const { data, error } = await query
        .order('week', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;
      setGames(data || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const completedGames = games.filter(g => g.is_completed && g.game_scores);
  const upcomingGames = games.filter(g => !g.is_completed);

  const getWeekCount = () => {
    if (!games.length) return 1;
    return Math.max(...games.map(g => g.week)) || 1;
  };

  const GameCard = ({ game }) => {
    const scores = game.game_scores;
    const isTeam1Winner = scores && scores.team1_score > scores.team2_score;
    const isTeam2Winner = scores && scores.team2_score > scores.team1_score;

    return (
      <div className="game-card">
        <div className="game-header">
          <div className="week-date">
            <span className="week">Week {game.week}</span>
            {game.scheduled_date && (
              <span className="date">
                {formatDateEST(game.scheduled_date)}
              </span>
            )}
          </div>
          <span className="game-status">
            {game.is_completed ? 'FINAL' : 'UPCOMING'}
          </span>
        </div>

        <div className="game-matchup">
          <div className={`team ${isTeam1Winner ? 'winner' : ''}`}>
            {game.team1?.logo_url && (
              <img src={game.team1.logo_url} alt={game.team1.name} className="team-logo" />
            )}
            <span className="team-name">{game.team1?.name}</span>
            {scores && (
              <span className={`score ${isTeam1Winner ? 'winner-score' : ''}`}>
                {scores.team1_score}
              </span>
            )}
          </div>

          <div className={`team ${isTeam2Winner ? 'winner' : ''}`}>
            {game.team2?.logo_url && (
              <img src={game.team2.logo_url} alt={game.team2.name} className="team-logo" />
            )}
            <span className="team-name">{game.team2?.name}</span>
            {scores && (
              <span className={`score ${isTeam2Winner ? 'winner-score' : ''}`}>
                {scores.team2_score}
              </span>
            )}
          </div>
        </div>

        {game.is_completed && game.highlights_url && (
          <div className="game-footer">
            <a href={game.highlights_url} target="_blank" rel="noopener noreferrer" className="highlights-link">
              🎬 Watch Highlights
            </a>
          </div>
        )}
      </div>
    );
  };

  if (!selectedSeason) {
    return (
      <div className="scores-page">
        <div className="scores-header">
          <h1>Scores</h1>
          <p>Please select a season to view scores</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scores-page">
      <div className="scores-header">
        <button className="game-results-btn">📅 Game Results</button>
        <h1>Scores</h1>
        <p className="scores-subtitle">Latest results and upcoming matchups</p>
      </div>

      <div className="scores-controls">
        <div className="season-selector">
          <label htmlFor="season-select">Season: </label>
          <select 
            id="season-select"
            value={selectedSeason ? String(selectedSeason.id) : ''}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
          >
            {availableSeasons.map(season => (
              <option key={season.id} value={String(season.id)}>
                {season.name} {season.is_active ? '(Current)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="week-selector">
          <label>Week: </label>
          <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}>
            <option value="all">All Weeks</option>
            {Array.from({ length: getWeekCount() }, (_, i) => i + 1).map(week => (
              <option key={week} value={week}>Week {week}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <div className={`tab ${activeTab === 'final' ? 'active' : ''}`} onClick={() => setActiveTab('final')}>
            Final ({completedGames.length})
          </div>
          <div className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`} onClick={() => setActiveTab('upcoming')}>
            Upcoming ({upcomingGames.length})
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading games...</div>
      ) : (
        <>
          {activeTab === 'final' && (
            <>
              {completedGames.length > 0 && (
                <div className="games-grid">
                  {completedGames.map(game => (
                    <GameCard key={game.id} game={game} />
                  ))}
                </div>
              )}
              {completedGames.length === 0 && (
                <div className="no-games">No completed games yet</div>
              )}
            </>
          )}

          {activeTab === 'upcoming' && (
            <>
              {upcomingGames.length > 0 && (
                <div className="games-grid">
                  {upcomingGames.map(game => (
                    <GameCard key={game.id} game={game} />
                  ))}
                </div>
              )}
              {upcomingGames.length === 0 && (
                <div className="no-games">No upcoming games</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ScoresDisplay;
